import type { IconProps } from "@legal-500/legal500-design-system";
import type { GlobalSearchItem, GlobalSearchSectionKey } from "../../api/search.service";
import { ROUTES } from "../../routes";

export const MIN_QUERY_LENGTH = 2;
export const MAX_ITEMS_PER_SECTION = 5;
export const SEARCH_DEBOUNCE_MS = 300;

type SectionConfig = {
  icon: IconProps["icon"];
  /** Fallback label, used when naming a source in the "unavailable" note. */
  label: string;
  itemHref: (item: GlobalSearchItem) => string;
  /** Only set when the list page reads a search param from the URL. */
  viewAllHref?: (query: string) => string;
};

function withParam(path: string, param: string, value: string): string {
  return `${path}?${new URLSearchParams({ [param]: value }).toString()}`;
}

export const SECTION_CONFIG: Record<GlobalSearchSectionKey, SectionConfig> = {
  companies: {
    icon: "firms-to-watch",
    label: "Companies",
    itemHref: item => ROUTES.addressBook.companyDetail(item.id),
    viewAllHref: q => withParam(ROUTES.addressBook.companies, "search", q),
  },
  contacts: {
    icon: "user",
    label: "Contacts",
    itemHref: item => ROUTES.addressBook.contactDetail(item.id),
    viewAllHref: q => withParam(ROUTES.addressBook.contacts, "search", q),
  },
  lawyerProfiles: {
    icon: "star",
    label: "Lawyer profiles",
    itemHref: item => ROUTES.profiles.lawyerProfileDetail(item.id),
    viewAllHref: q => withParam(ROUTES.profiles.lawyerProfiles, "q", q),
  },
  articles: {
    icon: "edit",
    label: "Articles",
    itemHref: item => ROUTES.profiles.articleDetail(item.id),
    viewAllHref: q => withParam(ROUTES.profiles.articles, "q", q),
  },
  teamPages: {
    icon: "bookmark",
    label: "Team pages",
    itemHref: item => ROUTES.profiles.teamPageDetail(item.id),
    viewAllHref: q => withParam(ROUTES.profiles.teamPages, "q", q),
  },
  organisations: {
    icon: "home",
    label: "Organisations",
    itemHref: item => ROUTES.users.organisationDetail(item.id),
    viewAllHref: q => withParam(ROUTES.users.organisations, "search", q),
  },
  staffUsers: {
    icon: "settings-cog",
    label: "Staff users",
    itemHref: item => ROUTES.users.detail(item.id),
    viewAllHref: q => withParam(ROUTES.users.staff, "search", q),
  },
  // The BPA and Region list pages have no detail route and don't read a
  // search param from the URL, so both items and "View all" go to the list.
  practiceAreas: {
    icon: "leaf",
    label: "Practice areas",
    itemHref: () => ROUTES.taxonomy.bpas,
  },
  regions: {
    icon: "globe",
    label: "Regions",
    itemHref: () => ROUTES.taxonomy.regions,
  },
  cmsPages: {
    icon: "copy",
    label: "CMS pages",
    itemHref: item => ROUTES.cms.pageDetail(item.id),
    viewAllHref: q => withParam(ROUTES.cms.pages, "search", q),
  },
};

export function isKnownSectionKey(key: string): key is GlobalSearchSectionKey {
  return Object.hasOwn(SECTION_CONFIG, key);
}
